<script lang="ts" setup>
import { ref, computed, getCurrentInstance, watch, onMounted } from "vue";
import DialogLayout from "@/components/common/DialogLayout.vue";
import CreateEditContactContent from "@/components/Contact/CreateEditContactContent.vue";
import { URMRoles } from "@/models/dto/userRoleManagement";
import {
  ContactCurrentUserPermissions,
  ContactGroup,
} from "@/models/dto/ContactGroup";
import ReservationReceivedContent from "./ReservationRecievedContent.vue";
import TripRecievedContent from "./TripRecievedContent.vue";
import { Customer, ReservationDetail } from "@/models/dto";
import customerService from "@/services/customer";
import auth from "@/store/modules/auth";
import SMSConfimationContent from "./SMSConfimationContent.vue";
import trip from "@/services/trip";
import {
  ContactType,
  getDisabledUserRoles,
} from "@/components/Contact/utils/helpers";
import reservationDetail from "@/services/reservationDetail";

const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);

const showDialog = ref(false);
const currentStepIndex = ref(0);
const customer = ref<Customer | null>(null);
const hasAddedContact = ref(false);

const emit = defineEmits(["input", "close", "add:contact"]);
const props = withDefaults(
  defineProps<{
    value: boolean;
    reservations: ReservationDetail[];
  }>(),
  {
    value: false,
  }
);

const showSMSConfirmation = ref(true);
const showMFAConfirmation = ref(false);
const phone = ref("");
const tripName = ref("");
const currentReservationIndex = ref(0);
const transitionName = ref("");
const hasAnimated = ref(false);
const contactGroups = ref<ContactGroup[]>([]);
const currentUserCreatePermissions = ref<ContactCurrentUserPermissions | null>(
  null
);

onMounted(async () => {
  await refreshCustomer();
  showDialog.value = props.value;
  await loadContactGroups();
});

const refreshCustomer = async (): Promise<void> => {
  const customerResponse = await customerService.byId(auth.userId);
  customer.value = customerResponse.data.customer;
  phone.value = customer.value?.phone;
  if (customer.value?.smsConfirmed) {
    showSMSConfirmation.value = false;
  }
};

const customerId = computed((): number | undefined => {
  return customer.value?.userId;
});

const reservationId = computed((): number | undefined => {
  return props.reservations?.[currentReservationIndex.value]?.reservationId;
});

const handleAddContact = async () => {
  try {
    if (tripName.value) {
      await trip.editRouteName(
        props.reservations?.[currentReservationIndex.value].tripId,
        tripName.value
      );
    }
    emit("add:contact");
    await loadContactGroups();
    hasAddedContact.value = true;
  } catch (error: any) {
    console.error(
      `Error updating trip name ${tripName.value} on reservation: ${
        reservationId.value
      } failed: ${error.response.data.message}`
    );
  }
};

const handleTripContentNext = async () => {
  if (currentReservationIndex.value === props.reservations?.length - 1) {
    goToNextStep();
  } else {
    transitionName.value = "slide-left";
    tripName.value = "";
    currentReservationIndex.value++;
    hasAddedContact.value = false;
  }
};

const handleTripContentBack = async () => {
  if (currentReservationIndex.value > 0) {
    transitionName.value = "slide-right";
    currentReservationIndex.value--;
    hasAddedContact.value = false;
  } else {
    goToPreviousStep();
  }
};

const loadContactGroups = async () => {
  if (!reservationId.value) return;
  const response = await reservationDetail.getContacts(
    reservationId.value
  );
  contactGroups.value = response.data.contactGroups;
  currentUserCreatePermissions.value =
    response.data.currentUserCreatePermissions;
};

const disabledUserRoles = computed(() => {
  return getDisabledUserRoles(currentUserCreatePermissions.value);
});

const handleSMSNext = async () => {
  showMFAConfirmation.value = true;
  goToNextStep();
};

const handleVerifyNext = async () => {
  showSMSConfirmation.value = false;
  showMFAConfirmation.value = false;
  goToNextStep();
};

const handleDidNotReceiveSMS = () => {
  goToPreviousStep();
};

const handleSMSSkip = () => {
  showMFAConfirmation.value = false;
  goToNextStep();
};

const contactNextText = computed(() => {
  if (hasAddedContact.value) {
    return t("common.NEXT").toString();
  }
  return t("common.SKIP").toString();
});

const defaultUserType = computed(() => {
  if (currentUserCreatePermissions.value?.canModifyAuthorizedUserRole) {
    return URMRoles.TripAuthorizedUser.key
  }
  return URMRoles.TripViewOnly.key
})

// Step definitions with visibility
const steps = computed(() => [
  {
    visible: showSMSConfirmation.value,
    content: TripRecievedContent,
    contentProps: {
      customerId: customerId.value,
      phone: phone.value,
    },
    headerActionRightText: t("common.SKIP").toString(),
    headerActionRight: () => handleSMSSkip(),
    on: {
      next: async () => await handleSMSNext(),
      "update:phone": (val) => (phone.value = val),
    },
  },
  {
    visible: showMFAConfirmation.value,
    content: SMSConfimationContent,
    contentProps: {
      customerId: customerId.value,
      phone: phone.value,
    },
    headerActionRightText: t("common.SKIP").toString(),
    headerActionRight: () => {
      showMFAConfirmation.value = false;
      goToNextStep();
    },
    headerActionLeftText: t("common.BACK").toString(),
    headerActionLeft: () => goToPreviousStep(),
    on: {
      confirm: () => handleVerifyNext(),
      retry: () => handleDidNotReceiveSMS(),
    },
  },
  {
    visible: true,
    content: CreateEditContactContent,
    contentProps: {
      title: t("common.SHARE_RESERVATION").toString(),
      subtitle: "Name your trip and invite others to help manage this booking!",
      isEditMode: false,
      contactGroups: contactGroups.value,
      defaultDisabledUserRoles: disabledUserRoles.value,
      defaultUserType: defaultUserType.value,
      headerActionRightText: t("common.SKIP").toString(),
      submitButtonText: t("common.SHARE").toString(),
      preventDefaultHeaderClose: true,
      reservation: props.reservations?.[currentReservationIndex.value],
      currentTripIndex: currentReservationIndex.value,
      tripCount: props.reservations?.length,
      tripName: tripName.value,
      showTripName: true,
      contactType: ContactType.Reservation,
      entityId: reservationId.value,
    },
    headerActionRightText: contactNextText.value,
    headerActionRight: () => handleTripContentNext(),
    ...(currentReservationIndex.value > 0 || showSMSConfirmation.value
      ? {
          headerActionLeftText: t("common.BACK").toString(),
          headerActionLeft: () => handleTripContentBack(),
        }
      : {}),
    on: {
      "update:tripName": (val) => (tripName.value = val),
      "add:contact": handleAddContact,
    },
  },
  {
    visible: true,
    content: ReservationReceivedContent,
    headerActionRightText: t("common.CLOSE").toString(),
    headerActionRight: () => closeDialog(),
    headerActionLeftText: t("common.BACK").toString(),
    headerActionLeft: () => goToPreviousStep(),
    on: {
      close: () => closeDialog(),
    },
  },
]);

const getNextVisibleStepIndex = (fromIndex: number) => {
  let idx = fromIndex + 1;
  while (idx < steps.value.length && !steps.value[idx].visible) {
    idx++;
  }
  return idx;
};

const getPreviousVisibleStepIndex = (fromIndex: number) => {
  let idx = fromIndex - 1;
  while (idx >= 0 && !steps.value[idx].visible) {
    idx--;
  }
  return idx;
};

const currentStep = computed(() => steps.value[currentStepIndex.value]);

// Watch for dialog open to enable animation on first step change
watch(
  () => showDialog.value,
  (newValue) => {
    if (newValue) {
      hasAnimated.value = true;
    }
  }
);

const goToNextStep = () => {
  transitionName.value = "slide-left";
  const nextIdx = getNextVisibleStepIndex(currentStepIndex.value);
  if (nextIdx < steps.value.length) {
    currentStepIndex.value = nextIdx;
  } else {
    closeDialog();
  }
};

const goToPreviousStep = () => {
  transitionName.value = "slide-right";
  const prevIdx = getPreviousVisibleStepIndex(currentStepIndex.value);
  if (prevIdx >= 0) {
    currentStepIndex.value = prevIdx;
  }
};

const closeDialog = () => {
  showDialog.value = false;
  emit("input", false);
  currentStepIndex.value = 0;
  currentReservationIndex.value = 0;
  transitionName.value = "";
  hasAnimated.value = false;
};

watch(
  () => props.value,
  (newValue) => {
    showDialog.value = newValue;
  }
);

const handleDialogInput = (val: boolean) => {
  if (!val) {
    closeDialog();
  } else {
    showDialog.value = val;
  }
};

const key = computed(() => {
  return (
    currentStep.value.contentProps?.title +
    "-" +
    phone.value +
    currentReservationIndex.value
  );
});

// Only reset when dialog opens
watch(
  () => showDialog.value,
  (dialogOpen) => {
    if (dialogOpen) {
      const firstVisibleIdx = steps.value.findIndex((step) => step.visible);
      currentStepIndex.value = firstVisibleIdx !== -1 ? firstVisibleIdx : 0;
    }
  }
);

// Also reset when showSMSConfirmation changes
watch(
  [() => showSMSConfirmation.value, () => showMFAConfirmation.value],
  () => {
    // If the current step is not visible, jump to the first visible step
    if (!steps.value[currentStepIndex.value].visible) {
      const firstVisibleIdx = steps.value.findIndex((step) => step.visible);
      currentStepIndex.value = firstVisibleIdx !== -1 ? firstVisibleIdx : 0;
    }
  }
);

// Watch for changes to currentReservationIndex
watch(
  () => currentReservationIndex.value,
  async () => {
    await loadContactGroups();
  }
);
</script>

<template>
  <DialogLayout
    :value="showDialog"
    @input="handleDialogInput"
    prevent-default-header-close
    persistent
    :header-action-right-text="currentStep.headerActionRightText"
    @click:header-action-right="currentStep.headerActionRight"
    v-bind="
      currentStep.headerActionLeftText
        ? { 'header-action-left-text': currentStep.headerActionLeftText }
        : {}
    "
    v-on="
      currentStep.headerActionLeft
        ? { 'click:header-action-left': currentStep.headerActionLeft }
        : {}
    "
    class="dialog-stepper"
  >
    <template #activator="slotProps">
      <slot name="activator" v-bind="slotProps" />
    </template>
    <template #contentLayout>
      <transition :name="transitionName" mode="out-in">
        <component
          v-if="currentStep.visible"
          :is="currentStep.content"
          v-bind="currentStep.contentProps"
          v-on="currentStep.on"
          :key="key"
        />
        <!-- Optionally, you can render a placeholder if not visible -->
        <div v-else style="min-height: 300px"></div>
      </transition>
    </template>
  </DialogLayout>
</template>

<style lang="scss" scoped>
@import "@/scss/colors";

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-fade-enter-to,
.slide-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-left-enter-from {
  opacity: 0;
  transform: translateX(40px);
}
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-left-enter-to,
.slide-left-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(-40px);
}
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(40px);
}
.slide-right-enter-to,
.slide-right-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.dialog-content-relative {
  position: relative;
  min-height: 300px;
}
</style>
